import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 0.5rem;
  color: #555;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #6c5ce7;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #6c5ce7;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #6c5ce7;
  }
`;

const SubmitButton = styled(motion.button)`
  background-color: #6c5ce7;
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 5px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #5a4ad1;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled(motion.div)`
  background-color: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 5px;
  margin-bottom: 1rem;
`;

const ErrorMessage = styled(motion.div)`
  background-color: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 5px;
  margin-bottom: 1rem;
`;

const ContactForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });

    const [status, setStatus] = useState({
        submitted: false,
        success: false,
        message: ''
    });

    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const fetchWithTimeout = async (url, options, timeout = 5000) => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);

            try {
                const response = await fetch(url, {
                    ...options,
                    signal: controller.signal
                });

                clearTimeout(timeoutId);
                return response;
            } catch (error) {
                clearTimeout(timeoutId);
                if (error.name === 'AbortError') {
                    console.log(`Request timed out after ${timeout}ms`);
                }
                throw error;
            }
        };

        try {
            // Build query parameters for the contact endpoint
            const queryParams = new URLSearchParams({
                phone_number: formData.phone,
                customer_name: formData.name,
                email: formData.email,
                message: `[${formData.subject}] ${formData.message}`
            });

            const contactUrl = `https://z184yo4b1e.execute-api.eu-west-1.amazonaws.com/prod/mlalazi/website/contact?${queryParams.toString()}`;

            // Send request to the contact endpoint with no-cors mode
            await fetchWithTimeout(
                contactUrl,
                {
                    method: "GET",
                    mode: 'no-cors' // Bypass CORS
                },
                5000
            );

            // Success
            setStatus({
                submitted: true,
                success: true,
                message: 'Your message has been sent successfully! We will get back to you soon.'
            });

            // Reset form after successful submission
            setFormData({
                name: '',
                email: '',
                subject: '',
                message: '',
                phone: ''
            });

            // Clear success message after 10 seconds
            setTimeout(() => {
                setStatus({
                    submitted: false,
                    success: false,
                    message: ''
                });
            }, 10000);

        } catch (error) {
            console.error('Form submission error:', error);
            setStatus({
                submitted: true,
                success: false,
                message: 'There was an error sending your message. Please try again later.'
            });

            // Clear error message after 10 seconds
            setTimeout(() => {
                setStatus({
                    submitted: false,
                    success: false,
                    message: ''
                });
            }, 10000);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {status.submitted && (
                status.success ? (
                    <SuccessMessage
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        {status.message}
                    </SuccessMessage>
                ) : (
                    <ErrorMessage
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        {status.message}
                    </ErrorMessage>
                )
            )}

            <Form onSubmit={handleSubmit}>
                <FormGroup>
                    <Label htmlFor="name">Your Name</Label>
                    <Input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                    />
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                    />
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                    />
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="subject">Subject</Label>
                    <Select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                    >
                        <option value="">Select a subject</option>
                        <option value="Event Inquiry">Event Inquiry</option>
                        <option value="Family History">Family History</option>
                        <option value="Registry Update">Add A Member</option>
                        <option value="Business Collaboration">Business Collaboration</option>
                        <option value="General Question">General Question</option>
                    </Select>
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="message">Your Message</Label>
                    <TextArea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                    />
                </FormGroup>

                <SubmitButton
                    type="submit"
                    disabled={loading}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    {loading ? 'Sending...' : 'Send Message'}
                </SubmitButton>
            </Form>
        </>
    );
};

export default ContactForm;